<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { NButton, NInput, NCard, NTag, NIcon, NAvatar } from 'naive-ui'
import { isAuthenticated, isProvider } from '@/services/auth'
import { Search, MapPin, MessageCircle, Globe, Heart, Shield, Clock, Star, ChevronRight, Bot, Stethoscope, Brain, Leaf, Users, UserPlus, FileText, CheckCircle, Upload, Eye, Lock, Sparkles, ArrowRight } from 'lucide-vue-next'
import { LANGUAGES, getCurrentLanguage, setCurrentLanguage, getLocaleFromPreferred } from '@/services/language'
import specializations from '@/data/specializations.json'
import doctorImage from '@/assets/images/luis-melendez-Pd4lRfKo16U-unsplash.jpg'
import { dt, getCurrentLocaleCode, getLocalizedLabel } from '@/utils/i18n' // Import the functions

const router = useRouter()
const { t, locale } = useI18n()
const searchQuery = ref("")
const selectedLanguage = ref(getCurrentLanguage())

// Animated search examples
const currentExample = ref('')
const currentExampleIndex = ref(0)
const currentCharIndex = ref(0)
const isTyping = ref(true)
let typewriterInterval = null

// Get examples based on current language - using full names
const searchExamples = computed(() => {
  const examples = {
    'english': [
      'Dr. Sharma Cardiology Mumbai',
      'Delhi Dr. Patel',
      'Dentist Bangalore',
      'Chennai Pediatrician'
    ],
    'hindi': [
      'डॉ. शर्मा कार्डियोलॉजी मुंबई',
      'दिल्ली डॉ. पटेल',
      'दंत चिकित्सक बैंगलोर',
      'जयपुर बाल रोग विशेषज्ञ'
    ],
    'bengali': [
      'ডাক্তার শর্মা কার্ডিওলজি কলকাতা',
      'ঢাকা ডাক্তার রহমান',
      'দন্ত চিকিৎসক চট্টগ্রাম',
      'শিশু বিশেষজ্ঞ সিলেট'
    ],
    'telugu': [
      'డాక్టర్ శర్మ కార్డియాలజీ హైదరాబాద్',
      'విజయవాడ డాక్టర్ రావు',
      'దంత వైద్యుడు బెంగళూరు',
      'చెన్నై పిల్లల వైద్యుడు'
    ],
    'marathi': [
      'डॉ. शर्मा हृदयरोगतज्ञ पुणे',
      'मुंबई डॉ. पाटील',
      'दंत चिकित्सक नागपूर',
      'कोल्हापूर बालरोगतज्ञ'
    ],
    'tamil': [
      'டாக்டர் ராம் இதய மருத்துவர் சென்னை',
      'கோயம்புத்தூர் டாக்டர் குமார்',
      'பல் மருத்துவர் மதுரை',
      'திருச்சி குழந்தைகள் மருত்துவர்'
    ],
    'gujarati': [
      'ડો. શાહ હૃદય નિષ્ણાત અમદાવાદ',
      'સુરત ડો. પટેલ',
      'દાંતના ડોકટર રાજકોટ',
      'બાળરોગ નિષ્ણાત વડોદરા'
    ],
    'kannada': [
      'ಡಾಕ್ಟರ್ ರಾಮ್ ಹೃದಯರೋಗ ತಜ್ಞ ಬೆಂಗಳೂರು',
      'ಮೈಸೂರು ಡಾಕ್ಟರ್ ಕುಮಾರ್',
      'ದಂತ ವೈದ್ಯ ಹುಬ್ಬಳ್ಳಿ',
      'ಮಂಗಳೂರು ಮಕ್ಕಳ ವೈದ್ಯ'
    ],
    'malayalam': [
      'ഡോക്ടർ രാജ് ഹൃദയരോഗ വിദഗ്ധൻ കൊച്ചി',
      'തിരുവനന്തപുരം ഡോക്ടർ നായർ',
      'ദന്ത ചികിത്സകൻ കോഴിക്കോട്',
      'കൊല്ലം കുട്ടികളുടെ ഡോക്ടർ'
    ],
    'odia': [
      'ଡାକ୍ତର ରାମ ହୃଦରୋଗ ବିଶେଷଜ୍ଞ ଭୁବନେଶ୍ୱର',
      'କଟକ ଡାକ୍ତର ପଟ୍ଟନାୟକ',
      'ଦନ୍ତ ଚିକିତ୍ସକ ବରହମପୁର',
      'ସମ୍ବଲପୁର ଶିଶୁ ବିଶେଷଜ୍ଞ'
    ],
    'punjabi': [
      'ਡਾਕਟਰ ਸਿੰਘ ਦਿਲ ਦਾ ਮਾਹਿਰ ਚੰਡੀਗੜ੍ਹ',
      'ਅੰਮ੍ਰਿਤਸਰ ਡਾਕਟਰ ਕੌਰ',
      'ਦੰਦਾਂ ਦਾ ਡਾਕਟਰ ਜਲੰਧਰ',
      'ਲੁਧਿਆਣਾ ਬੱਚਿਆਂ ਦਾ ਡਾਕਟਰ'
    ],
    'urdu': [
      'ڈاکٹر احمد دل کے ماہر کراچی',
      'لاہور ڈاکٹر خان',
      'دانتوں کا ڈاکٹر اسلام آباد',
      'راولپنڈی بچوں کے ڈاکٹر'
    ]
  }

  return examples[selectedLanguage.value] || examples['english']
})

// Watch for language changes and reset typewriter
watch(selectedLanguage, () => {
  // Clear existing interval
  if (typewriterInterval) {
    clearInterval(typewriterInterval)
  }

  // Reset all state
  currentExampleIndex.value = 0
  currentCharIndex.value = 0
  currentExample.value = ''
  isTyping.value = true

  // Restart the typewriter with new language examples
  setTimeout(() => {
    typewriterInterval = setInterval(() => {
      typewriterEffect()
    }, 100)
  }, 100)
})

const typewriterEffect = () => {
  const currentExamples = searchExamples.value

  // Safety check - return early if examples not ready
  if (!currentExamples || currentExamples.length === 0) {
    return
  }

  // Reset index if it's out of bounds
  if (currentExampleIndex.value >= currentExamples.length) {
    currentExampleIndex.value = 0
    currentCharIndex.value = 0
    isTyping.value = true
    return
  }

  const example = currentExamples[currentExampleIndex.value]

  if (isTyping.value) {
    if (currentCharIndex.value < example.length) {
      currentExample.value = example.substring(0, currentCharIndex.value + 1)
      currentCharIndex.value++
    } else {
      // Finished typing, wait then start deleting
      setTimeout(() => {
        isTyping.value = false
      }, 2000)
      return
    }
  } else {
    if (currentCharIndex.value > 0) {
      currentExample.value = example.substring(0, currentCharIndex.value - 1)
      currentCharIndex.value--
    } else {
      // Finished deleting, move to next example
      isTyping.value = true
      currentExampleIndex.value = (currentExampleIndex.value + 1) % currentExamples.length
    }
  }
}

// Get current locale code reactively
const currentLocale = computed(() => getCurrentLocaleCode())

const featuredSpecializations = computed(() => {
  const featured = []
  const icons = {
    'Conventional Medicine': Stethoscope,
    'Traditional Indian Medicine (AYUSH)': Leaf,
    'Alternative & Complementary Medicine': Heart,
    'Mental Health': Brain,
    'Allied Health': Users
  }

  specializations.categories.forEach(cat => {
    const catSpecs = cat.specializations.slice(0, 2).map(s => ({
      ...s,
      category: cat.name,
      categoryLabel: getLocalizedLabel(cat, currentLocale.value), // Add localized category name
      localizedLabel: getLocalizedLabel(s, currentLocale.value), // Add localized specialization label
      icon: icons[cat.name] || Stethoscope
    }))
    featured.push(...catSpecs)
  })
  return featured.slice(0, 10)
})

// Computed for localized categories (if you need them elsewhere)
const localizedCategories = computed(() => {
  return specializations.categories.map(cat => ({
    ...cat,
    localizedName: getLocalizedLabel(cat, currentLocale.value),
    specializations: cat.specializations.map(spec => ({
      ...spec,
      localizedLabel: getLocalizedLabel(spec, currentLocale.value)
    }))
  }))
})

onMounted(() => {
  locale.value = getLocaleFromPreferred(getCurrentLanguage())

  // Start typewriter effect with a small delay to ensure everything is initialized
  setTimeout(() => {
    typewriterInterval = setInterval(() => {
      typewriterEffect()
    }, 100)
  }, 500)
})

onUnmounted(() => {
  if (typewriterInterval) {
    clearInterval(typewriterInterval)
  }
})

const handleLanguageChange = (value) => {
  selectedLanguage.value = value
  setCurrentLanguage(value)
  locale.value = getLocaleFromPreferred(value)
}

const handleSearch = () => {
  const params = new URLSearchParams()
  if (searchQuery.value) params.append('q', searchQuery.value)
  params.append('language', selectedLanguage.value)
  router.push({ path: '/search', query: Object.fromEntries(params) })
}

const goToSpecializations = () => {
  router.push('/specializations')
}

const searchBySpecialization = (spec) => {
  router.push({
    path: '/search',
    query: {
      q: spec.localizedLabel || spec.label, // Use localized label
      language: selectedLanguage.value
    }
  })
}

const startChat = () => {
  if (!isAuthenticated.value) {
    router.push('/login?redirect=/chat')
    return
  }

  if (isProvider.value) {
    router.push('/provider/chat')
  } else {
    router.push('/patient/chat')
  }
}
</script>

<template>
  <div class="homepage">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-bg-pattern"></div>
      <div class="hero-gradient-orb orb-1"></div>
      <div class="hero-gradient-orb orb-2"></div>
      <div class="container">
        <div class="hero-grid">
          <div class="hero-content">
            <div class="hero-badge">
              <span class="badge-icon">🌍</span>
              <span>{{ dt('home.hero.badge') }}</span>
              <span class="badge-shine"></span>
            </div>
            <h1 class="hero-title">
              {{ dt('home.hero.title') }}
              <span class="highlight">{{ dt('home.hero.titleHighlight') }}</span>
            </h1>
            <p class="hero-subtitle">
              {{ dt('home.hero.subtitle') }}
            </p>

            <div class="search-box">
              <div class="search-input-wrapper">
                <NIcon :component="Search" size="22" class="search-icon" />
                <NInput v-model:value="searchQuery" :placeholder="dt('home.hero.searchPlaceholder')"
                  class="search-input" size="large" :bordered="false" @keyup.enter="handleSearch" />
              </div>
              <NButton type="primary" size="large" class="search-btn" @click="handleSearch">
                <NIcon :component="Search" size="18" style="margin-right: 8px;" />
                {{ dt('home.hero.searchButton') }}
              </NButton>
            </div>

            <div class="search-examples">
              <span class="examples-label">Try: </span>
              <span class="animated-example">{{ currentExample }}</span>
            </div>
          </div>

          <div class="hero-image-wrapper">
            <div class="hero-image-container">
              <div class="image-glow"></div>
              <img :src="doctorImage" alt="Healthcare" class="hero-img" />

              <div class="floating-card card-1">
                <div class="card-icon green">
                  <NIcon :component="Shield" size="22" />
                </div>
                <div class="card-text">
                  <strong>{{ dt('home.hero.cards.verified.title') }}</strong>
                  <span>{{ dt('home.hero.cards.verified.subtitle') }}</span>
                </div>
              </div>

              <div class="floating-card card-2">
                <div class="card-icon blue">
                  <NIcon :component="Lock" size="22" />
                </div>
                <div class="card-text">
                  <strong>{{ dt('home.hero.cards.encrypted.title') }}</strong>
                  <span>{{ dt('home.hero.cards.encrypted.subtitle') }}</span>
                </div>
              </div>

              <div class="floating-card card-3" @click="startChat">
                <div class="card-icon purple">
                  <NIcon :component="MessageCircle" size="22" />
                </div>
                <div class="card-text">
                  <strong>{{ dt('home.hero.cards.aiChat.title') }}</strong>
                  <span>{{ dt('home.hero.cards.aiChat.subtitle') }}</span>
                </div>
                <NIcon :component="ArrowRight" size="16" class="card-arrow" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Specializations Section -->
    <section class="specializations">
      <div class="container">
        <div class="section-header">
          <div>
            <span class="section-label">{{ dt('home.specializations.label') }}</span>
            <h2 class="section-title">{{ dt('home.specializations.title') }}</h2>
            <p class="section-subtitle">{{ dt('home.specializations.subtitle') }}</p>
          </div>
          <NButton text @click="goToSpecializations" class="view-all-btn">
            {{ dt('home.specializations.viewAll') }}
            <template #icon>
              <NIcon :component="ChevronRight" />
            </template>
          </NButton>
        </div>

        <div class="spec-grid">
          <div v-for="spec in featuredSpecializations" :key="spec.value" class="spec-card"
            @click="searchBySpecialization(spec)">
            <div class="spec-icon-wrapper">
              <NIcon :component="spec.icon" size="26" />
            </div>
            <div class="spec-info">
              <!-- Use localized label -->
              <h4>{{ spec.localizedLabel }}</h4>
              <!-- Use localized category name -->
              <span class="spec-category">{{ spec.categoryLabel }}</span>
            </div>
            <div class="spec-arrow">
              <NIcon :component="ChevronRight" size="20" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- AI Assistant Card -->
    <section class="ai-section">
      <div class="container">
        <div class="ai-card">
          <div class="ai-bg-pattern"></div>
          <div class="ai-content">
            <div class="ai-icon-wrapper">
              <NIcon :component="Sparkles" size="32" color="#fff" />
              <div class="ai-icon-ring"></div>
            </div>
            <div class="ai-text">
              <span class="ai-badge">{{ dt('home.ai.badge') }}</span>
              <h3>{{ dt('home.ai.title') }}</h3>
              <p>{{ dt('home.ai.description') }}</p>
              <NButton size="large" class="ai-btn" @click="startChat">
                <NIcon :component="MessageCircle" size="18" />
                {{ dt('home.ai.button') }}
                <NIcon :component="ArrowRight" size="18" />
              </NButton>
            </div>
          </div>
          <div class="ai-languages">
            <span class="ai-lang-label">{{ dt('home.ai.availableIn') }}</span>
            <div class="ai-lang-tags">
              <span v-for="lang in ['తెలుగు', 'हिंदी', 'তমিழ்', 'ಕನ್ನಡ', 'മലയാളം', 'বাংলা']" :key="lang"
                class="ai-lang">
                {{ lang }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Timeline Section -->
    <section class="timeline-section">
      <div class="container">
        <div class="timeline-header">
          <span class="section-label">{{ dt('home.timeline.label') }}</span>
          <h2 class="section-title center">{{ dt('home.timeline.title') }}</h2>
          <p class="section-subtitle center">{{ dt('home.timeline.subtitle') }}</p>
        </div>

        <div class="timeline">
          <div class="timeline-line"></div>

          <div class="timeline-item">
            <div class="timeline-dot"><span>1</span></div>
            <div class="timeline-content">
              <div class="timeline-icon-wrapper">
                <NIcon :component="UserPlus" size="24" />
              </div>
              <div class="timeline-info">
                <h3>{{ dt('home.timeline.steps.step1.title') }}</h3>
                <p>{{ dt('home.timeline.steps.step1.description') }}</p>
              </div>
            </div>
          </div>

          <div class="timeline-item">
            <div class="timeline-dot"><span>2</span></div>
            <div class="timeline-content">
              <div class="timeline-icon-wrapper">
                <NIcon :component="FileText" size="24" />
              </div>
              <div class="timeline-info">
                <h3>{{ dt('home.timeline.steps.step2.title') }}</h3>
                <p>{{ dt('home.timeline.steps.step2.description') }}</p>
                <div class="timeline-highlight">
                  <NIcon :component="Globe" size="16" />
                  <span>{{ dt('home.timeline.steps.step2.highlight') }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="timeline-item">
            <div class="timeline-dot"><span>3</span></div>
            <div class="timeline-content">
              <div class="timeline-icon-wrapper">
                <NIcon :component="Upload" size="24" />
              </div>
              <div class="timeline-info">
                <h3>{{ dt('home.timeline.steps.step3.title') }}</h3>
                <p>{{ dt('home.timeline.steps.step3.description') }}</p>
              </div>
            </div>
          </div>

          <div class="timeline-item">
            <div class="timeline-dot"><span>4</span></div>
            <div class="timeline-content">
              <div class="timeline-icon-wrapper">
                <NIcon :component="Eye" size="24" />
              </div>
              <div class="timeline-info">
                <h3>{{ dt('home.timeline.steps.step4.title') }}</h3>
                <p>{{ dt('home.timeline.steps.step4.description') }}</p>
              </div>
            </div>
          </div>

          <div class="timeline-item">
            <div class="timeline-dot success">
              <NIcon :component="CheckCircle" size="20" />
            </div>
            <div class="timeline-content success">
              <div class="timeline-icon-wrapper success">
                <NIcon :component="CheckCircle" size="24" />
              </div>
              <div class="timeline-info">
                <h3>{{ dt('home.timeline.steps.step5.title') }}</h3>
                <p>{{ dt('home.timeline.steps.step5.description') }}</p>
                <div class="timeline-highlight success">
                  <NIcon :component="Users" size="16" />
                  <span>{{ dt('home.timeline.steps.step5.highlight') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="timeline-cta">
          <NButton type="primary" size="large" @click="router.push('/register?role=provider')">
            {{ dt('home.timeline.cta') }}
            <template #icon>
              <NIcon :component="ArrowRight" />
            </template>
          </NButton>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="stats-section">
      <div class="container">
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-icon">🌐</div>
            <div class="stat-number">10+</div>
            <div class="stat-label">{{ dt('home.stats.languages') }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-icon">🩺</div>
            <div class="stat-number">50+</div>
            <div class="stat-label">{{ dt('home.stats.specializations') }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-icon">📋</div>
            <div class="stat-number">100+</div>
            <div class="stat-label">{{ dt('home.stats.procedures') }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-icon">📍</div>
            <div class="stat-number">28</div>
            <div class="stat-label">{{ dt('home.stats.states') }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Languages Section -->
    <section class="languages-section">
      <div class="container">
        <span class="section-label center">{{ dt('home.languages.label') }}</span>
        <h2 class="section-title center">{{ dt('home.languages.title') }}</h2>
        <p class="section-subtitle center">{{ dt('home.languages.subtitle') }}</p>

        <div class="language-grid">
          <div v-for="lang in LANGUAGES.slice(0, 12)" :key="lang.value" class="language-card"
            :class="{ active: selectedLanguage === lang.value }" @click="handleLanguageChange(lang.value)">
            <span class="lang-native">{{ lang.native || lang.label }}</span>
            <span class="lang-name">{{ lang.label }}</span>
            <span class="lang-check" v-if="selectedLanguage === lang.value">
              <NIcon :component="CheckCircle" size="18" />
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- Medicine Systems -->
    <section class="medicine-systems">
      <div class="container">
        <span class="section-label center">{{ dt('home.medicineSystems.label') }}</span>
        <h2 class="section-title center">{{ dt('home.medicineSystems.title') }}</h2>
        <p class="section-subtitle center">{{ dt('home.medicineSystems.subtitle') }}</p>

        <div class="systems-grid">
          <div class="system-card">
            <div class="system-icon">🏥</div>
            <h4>{{ dt('home.medicineSystems.systems.modern.title') }}</h4>
            <p>{{ dt('home.medicineSystems.systems.modern.description') }}</p>
          </div>
          <div class="system-card">
            <div class="system-icon">🌿</div>
            <h4>{{ dt('home.medicineSystems.systems.ayurveda.title') }}</h4>
            <p>{{ dt('home.medicineSystems.systems.ayurveda.description') }}</p>
          </div>
          <div class="system-card">
            <div class="system-icon">🧘</div>
            <h4>{{ dt('home.medicineSystems.systems.yoga.title') }}</h4>
            <p>{{ dt('home.medicineSystems.systems.yoga.description') }}</p>
          </div>
          <div class="system-card">
            <div class="system-icon">💊</div>
            <h4>{{ dt('home.medicineSystems.systems.homeopathy.title') }}</h4>
            <p>{{ dt('home.medicineSystems.systems.homeopathy.description') }}</p>
          </div>
          <div class="system-card">
            <div class="system-icon">🌙</div>
            <h4>{{ dt('home.medicineSystems.systems.unani.title') }}</h4>
            <p>{{ dt('home.medicineSystems.systems.unani.description') }}</p>
          </div>
          <div class="system-card">
            <div class="system-icon">🍃</div>
            <h4>{{ dt('home.medicineSystems.systems.siddha.title') }}</h4>
            <p>{{ dt('home.medicineSystems.systems.siddha.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="cta-bg-pattern"></div>
      <div class="container">
        <div class="cta-content">
          <span class="cta-badge">{{ dt('home.cta.badge') }}</span>
          <h2>{{ dt('home.cta.title') }}</h2>
          <p>{{ dt('home.cta.description') }}</p>
          <div class="cta-buttons">
            <NButton type="primary" size="large" class="cta-btn-primary"
              @click="router.push('/register?role=provider')">
              {{ dt('home.cta.primaryButton') }}
              <template #icon>
                <NIcon :component="ArrowRight" />
              </template>
            </NButton>
            <NButton size="large" ghost class="cta-btn-secondary" @click="router.push('/about')">
              {{ dt('home.cta.secondaryButton') }}
            </NButton>
          </div>
          <div class="cta-features">
            <span>
              <NIcon :component="CheckCircle" size="16" /> {{ dt('home.cta.features.free') }}
            </span>
            <span>
              <NIcon :component="CheckCircle" size="16" /> {{ dt('home.cta.features.verified') }}
            </span>
            <span>
              <NIcon :component="CheckCircle" size="16" /> {{ dt('home.cta.features.languages') }}
            </span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.homepage {
  min-height: 100vh;
  background: #fff;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.section-label {
  display: inline-block;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #6366f1;
  margin-bottom: 12px;
}

.section-label.center {
  display: block;
  text-align: center;
}

/* ========== HERO ========== */
.hero {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #1e3a5f 100%);
  color: white;
  padding: 100px 0 120px;
  position: relative;
  overflow: hidden;
}

.hero-bg-pattern {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.12) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.08) 0%, transparent 40%),
    radial-gradient(circle at 40% 80%, rgba(139, 92, 246, 0.08) 0%, transparent 40%);
  pointer-events: none;
}

.hero-gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  pointer-events: none;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  top: -100px;
  right: 10%;
  animation: float-slow 8s ease-in-out infinite;
}

.orb-2 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #10b981, #06b6d4);
  bottom: -50px;
  left: 5%;
  animation: float-slow 10s ease-in-out infinite reverse;
}

@keyframes float-slow {

  0%,
  100% {
    transform: translate(0, 0);
  }

  50% {
    transform: translate(30px, -30px);
  }
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  position: relative;
  z-index: 1;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  padding: 10px 20px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 28px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  position: relative;
  overflow: hidden;
}

.badge-icon {
  font-size: 16px;
}

.badge-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  animation: shine 3s infinite;
}

@keyframes shine {
  0% {
    left: -100%;
  }

  50%,
  100% {
    left: 100%;
  }
}

.hero-title {
  font-size: 3.8rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 24px;
  letter-spacing: -1px;
}

.hero-title .highlight {
  background: linear-gradient(135deg, #10b981 0%, #6366f1 50%, #8b5cf6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.25rem;
  opacity: 0.85;
  margin-bottom: 36px;
  line-height: 1.7;
  max-width: 540px;
}

.search-box {
  display: flex;
  background: white;
  border-radius: 20px;
  padding: 8px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1);
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
  padding: 0 20px;
}

.search-icon {
  color: #94a3b8;
  margin-right: 14px;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
}

.search-input :deep(input) {
  font-size: 16px;
  color: #1e293b;
}

.search-input :deep(input)::placeholder {
  color: #94a3b8;
}

.search-btn {
  padding: 0 28px;
  border-radius: 14px;
  font-weight: 600;
  height: 52px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border: none;
  transition: all 0.3s;
}

.search-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(99, 102, 241, 0.4);
}

.search-examples {
  margin-top: 16px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.examples-label {
  margin-right: 8px;
}

.animated-example {
  color: #4ade80;
  font-weight: 500;
  min-height: 1.2em;
  display: inline-block;
}

.animated-example::after {
  content: '|';
  animation: blink 1s infinite;
  color: #4ade80;
  margin-left: 2px;
}

@keyframes blink {

  0%,
  50% {
    opacity: 1;
  }

  51%,
  100% {
    opacity: 0;
  }
}

/* Hero Image */
.hero-image-wrapper {
  display: flex;
  justify-content: center;
  perspective: 1000px;
}

.hero-image-container {
  position: relative;
}

.image-glow {
  position: absolute;
  inset: -20px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(139, 92, 246, 0.3));
  border-radius: 32px;
  filter: blur(40px);
  z-index: -1;
}

.hero-img {
  width: 100%;
  max-width: 480px;
  border-radius: 24px;
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
}

/* Floating Cards */
.floating-card {
  position: absolute;
  background: white;
  padding: 16px 20px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  animation: float 4s ease-in-out infinite;
  transition: all 0.3s;
}

.floating-card:hover {
  transform: scale(1.05) translateY(-5px);
}

.card-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-icon.green {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #059669;
}

.card-icon.blue {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #2563eb;
}

.card-icon.purple {
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #7c3aed;
}

.card-text {
  display: flex;
  flex-direction: column;
}

.card-text strong {
  color: #1e293b;
  font-size: 15px;
  font-weight: 600;
}

.card-text span {
  color: #64748b;
  font-size: 12px;
}

.card-arrow {
  color: #94a3b8;
  transition: all 0.2s;
}

.floating-card.card-3:hover .card-arrow {
  color: #7c3aed;
  transform: translateX(4px);
}

.card-1 {
  top: 15%;
  left: -40px;
  animation-delay: 0s;
}

.card-2 {
  bottom: 25%;
  right: -40px;
  animation-delay: 1.5s;
}

.card-3 {
  bottom: 10%;
  left: -20px;
  animation-delay: 0.8s;
  cursor: pointer;
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-12px);
  }
}

/* ========== SPECIALIZATIONS ========== */
.specializations {
  padding: 100px 0;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 48px;
}

.section-title {
  font-size: 2.4rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
}

.section-title.center {
  text-align: center;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #64748b;
  line-height: 1.6;
}

.section-subtitle.center {
  text-align: center;
  max-width: 600px;
  margin: 0 auto 48px;
}

.view-all-btn {
  font-size: 15px;
  font-weight: 600;
  color: #6366f1;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
}

.view-all-btn:hover {
  gap: 8px;
}

.spec-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.spec-card {
  display: flex;
  align-items: center;
  gap: 18px;
  background: white;
  padding: 22px 26px;
  border-radius: 18px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid #e2e8f0;
  position: relative;
  overflow: hidden;
}

.spec-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.05), rgba(139, 92, 246, 0.05));
  opacity: 0;
  transition: opacity 0.3s;
}

.spec-card:hover {
  border-color: #6366f1;
  box-shadow: 0 8px 30px rgba(99, 102, 241, 0.12);
  transform: translateY(-3px);
}

.spec-card:hover::before {
  opacity: 1;
}

.spec-icon-wrapper {
  width: 58px;
  height: 58px;
  background: linear-gradient(135deg, #ede9fe, #e0e7ff);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366f1;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.spec-info {
  flex: 1;
  position: relative;
  z-index: 1;
}

.spec-info h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.spec-category {
  font-size: 13px;
  color: #94a3b8;
}

.spec-arrow {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  transition: all 0.3s;
  position: relative;
  z-index: 1;
}

.spec-card:hover .spec-arrow {
  background: #6366f1;
  color: white;
  transform: translateX(4px);
}

/* ========== AI SECTION ========== */
.ai-section {
  padding: 0 0 100px;
  background: white;
}

.ai-card {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
  border-radius: 28px;
  padding: 56px;
  position: relative;
  overflow: hidden;
}

.ai-bg-pattern {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 90% 10%, rgba(255, 255, 255, 0.15) 0%, transparent 40%),
    radial-gradient(circle at 10% 90%, rgba(255, 255, 255, 0.1) 0%, transparent 40%);
  pointer-events: none;
}

.ai-content {
  display: flex;
  align-items: center;
  gap: 40px;
  position: relative;
  z-index: 1;
}

.ai-icon-wrapper {
  width: 88px;
  height: 88px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
}

.ai-icon-ring {
  position: absolute;
  inset: -4px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 28px;
  animation: pulse-ring 2s infinite;
}

@keyframes pulse-ring {

  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }

  50% {
    transform: scale(1.1);
    opacity: 0.5;
  }
}

.ai-text {
  color: white;
}

.ai-badge {
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 16px;
}

.ai-text h3 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 14px;
  line-height: 1.2;
}

.ai-text p {
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 24px;
  line-height: 1.7;
  max-width: 500px;
}

.ai-btn {
  background: white;
  color: #6366f1;
  border: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  border-radius: 14px;
  transition: all 0.3s;
}

.ai-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
}

.ai-languages {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 32px;
  padding-top: 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  z-index: 1;
}

.ai-lang-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 500;
}

.ai-lang-tags {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.ai-lang {
  background: rgba(255, 255, 255, 0.15);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 15px;
  color: white;
  font-weight: 500;
}

/* ========== TIMELINE ========== */
.timeline-section {
  padding: 100px 0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.timeline-header {
  text-align: center;
  margin-bottom: 60px;
}

.timeline {
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
}

.timeline-line {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, #e2e8f0 0%, #6366f1 50%, #10b981 100%);
  transform: translateX(-50%);
  border-radius: 3px;
}

.timeline-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 48px;
  position: relative;
}

.timeline-item:last-child {
  margin-bottom: 0;
}

.timeline-dot {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 14px;
  z-index: 2;
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
}

.timeline-dot.success {
  background: linear-gradient(135deg, #10b981, #059669);
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
}

.timeline-content {
  width: calc(50% - 50px);
  background: white;
  padding: 28px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  display: flex;
  gap: 20px;
  transition: all 0.3s;
}

.timeline-content:hover {
  border-color: #6366f1;
  box-shadow: 0 10px 40px rgba(99, 102, 241, 0.1);
  transform: translateY(-3px);
}

.timeline-content.success:hover {
  border-color: #10b981;
  box-shadow: 0 10px 40px rgba(16, 185, 129, 0.1);
}

.timeline-item:nth-child(odd) .timeline-content {
  margin-right: auto;
}

.timeline-item:nth-child(even) .timeline-content {
  margin-left: auto;
}

.timeline-icon-wrapper {
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, #ede9fe, #e0e7ff);
  color: #6366f1;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.timeline-icon-wrapper.success {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #059669;
}

.timeline-info h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 10px;
}

.timeline-info p {
  font-size: 0.95rem;
  color: #64748b;
  line-height: 1.6;
}

.timeline-highlight {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #ede9fe, #e0e7ff);
  border-radius: 12px;
  font-size: 13px;
  color: #6366f1;
  font-weight: 600;
}

.timeline-highlight.success {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #059669;
}

.timeline-cta {
  text-align: center;
  margin-top: 56px;
}

/* ========== STATS ========== */
.stats-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
}

.stat-item {
  text-align: center;
  padding: 24px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s;
}

.stat-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-5px);
}

.stat-icon {
  font-size: 2.5rem;
  margin-bottom: 16px;
}

.stat-number {
  font-size: 3.2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #10b981, #6366f1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 1rem;
  opacity: 0.8;
  font-weight: 500;
}

/* ========== LANGUAGES ========== */
.languages-section {
  padding: 100px 0;
  background: #f8fafc;
}

.language-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  max-width: 900px;
  margin: 0 auto;
}

.language-card {
  background: white;
  padding: 24px 16px;
  border-radius: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
}

.language-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.05), rgba(139, 92, 246, 0.05));
  opacity: 0;
  transition: opacity 0.3s;
}

.language-card:hover {
  border-color: #6366f1;
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(99, 102, 241, 0.12);
}

.language-card:hover::before {
  opacity: 1;
}

.language-card.active {
  border-color: #6366f1;
  background: linear-gradient(135deg, #ede9fe, #e0e7ff);
}

.lang-native {
  display: block;
  font-size: 1.3rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
}

.lang-name {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.lang-check {
  position: absolute;
  top: 10px;
  right: 10px;
  color: #6366f1;
}

/* ========== MEDICINE SYSTEMS ========== */
.medicine-systems {
  padding: 100px 0;
  background: white;
}

.systems-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.system-card {
  text-align: center;
  padding: 40px 28px;
  border-radius: 20px;
  background: #f8fafc;
  transition: all 0.3s;
  border: 1px solid transparent;
}

.system-card:hover {
  background: linear-gradient(135deg, #ede9fe, #e0e7ff);
  transform: translateY(-5px);
  border-color: #c7d2fe;
}

.system-icon {
  font-size: 3.5rem;
  margin-bottom: 20px;
}

.system-card h4 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 10px;
}

.system-card p {
  font-size: 0.95rem;
  color: #64748b;
  line-height: 1.5;
}

/* ========== CTA ========== */
.cta-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%);
  color: white;
  position: relative;
  overflow: hidden;
}

.cta-bg-pattern {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 40%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 40%);
  pointer-events: none;
}

.cta-content {
  text-align: center;
  max-width: 650px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.cta-badge {
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 20px;
}

.cta-content h2 {
  font-size: 2.8rem;
  font-weight: 800;
  margin-bottom: 18px;
  line-height: 1.2;
}

.cta-content p {
  font-size: 1.2rem;
  opacity: 0.95;
  margin-bottom: 36px;
  line-height: 1.7;
}

.cta-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 32px;
}

.cta-btn-primary {
  background: white;
  color: #059669;
  border: none;
  font-weight: 700;
  padding: 16px 32px;
  border-radius: 14px;
  font-size: 16px;
  transition: all 0.3s;
}

.cta-btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
}

.cta-btn-secondary {
  border-color: rgba(255, 255, 255, 0.5);
  color: white;
  font-weight: 600;
  padding: 16px 32px;
  border-radius: 14px;
  font-size: 16px;
}

.cta-btn-secondary:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: white;
}

.cta-features {
  display: flex;
  gap: 32px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-features span {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 500;
  opacity: 0.95;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 1024px) {
  .hero-grid {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 60px;
  }

  .hero-subtitle {
    max-width: 100%;
  }

  .hero-image-wrapper {
    display: none;
  }

  .spec-grid {
    grid-template-columns: 1fr;
  }

  .timeline-line {
    left: 30px;
  }

  .timeline-dot {
    left: 30px;
  }

  .timeline-content {
    width: calc(100% - 80px);
    margin-left: auto !important;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .language-grid {
    grid-template-columns: repeat(4, 1fr);
  }

  .systems-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .hero {
    padding: 70px 0 90px;
  }

  .hero-title {
    font-size: 2.4rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .search-box {
    flex-direction: column;
    gap: 12px;
    padding: 12px;
  }

  .search-btn {
    width: 100%;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .ai-content {
    flex-direction: column;
    text-align: center;
  }

  .ai-text h3 {
    font-size: 1.5rem;
  }

  .ai-languages {
    flex-direction: column;
    align-items: center;
  }

  .ai-lang-tags {
    justify-content: center;
  }

  .timeline-content {
    width: calc(100% - 60px);
    flex-direction: column;
  }

  .timeline-dot {
    left: 20px;
    width: 32px;
    height: 32px;
    font-size: 12px;
  }

  .timeline-line {
    left: 20px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .stat-number {
    font-size: 2.4rem;
  }

  .language-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .systems-grid {
    grid-template-columns: 1fr;
  }

  .cta-content h2 {
    font-size: 2rem;
  }

  .cta-buttons {
    flex-direction: column;
  }

  .cta-features {
    flex-direction: column;
    gap: 12px;
  }
}
</style>